import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import EM_API from "common-ui-components/components/utils/API";
import TableManager from "common-ui-components/components/table-manager";
import CircularProgress from "@mui/material/CircularProgress";
import PostPatchScanNotifier from "../patch/components/PatchScanNotifier/PostPatchScanNotifier";
import { useSelector } from "react-redux";
import Message from "../../common-ui-components/components/messager/Message";
import { updateDeviceScanStatus } from "reducers/inventoryReducer";
import { useRestrictionChecker } from "common-ui-components/components/restrictionUI/RestrictionChecker";
import { notify } from "common-ui-components/components/notification/Notification";
import { Failure } from "common-ui-components/components/notification/templates";
import { updatePatchScanDeviceStatus } from "reducers/patchReducer";
import CommonManagedDevice from "app/common/managed-device/ManagedDevice";
import { useLocation } from 'react-router-dom';
import { getCommonGroupFilters } from "api/org/CommonGroupsFilter";

const tableURLMap = {
  inventory: "/devices/get-scan",
  patch: "/devices/get-scan?type=patch",
};

const scanDeviceURL = {
  inventory: "/devices/on-demand-scan?scanLevel=DIFF",
  patch: "/patch/on-demand-scan",
};

const scanAllDeviceURL = {
  inventory: "/devices/on-demand-scan-all",
  patch: "/patch/on-demand-scan-all",
};

const getDeviceScanState = (deviceScanStatusArr, record) =>
  deviceScanStatusArr.find(
    (deviceScanStatus) => deviceScanStatus.deviceId === record.id,
  );

const getEffectiveScanStatus = (deviceScanStatusArr, record) => {
  const deviceState = getDeviceScanState(deviceScanStatusArr, record);
  return deviceState ? deviceState.scanStatus : record.status;
};

export const ManagedDevice = (props) => <CommonManagedDevice {...props} />;

const ScanStatus = ({ record, tab = "inventory" }) => {
  const inventoryScanStatusArr = useSelector(
    (state) => state.inventory.deviceScanStatusArr,
  );
  const patchScanDeviceStatus = useSelector(
    (state) => state.patch.patchScanDeviceStatusArr || [],
  );
  const statusSource =
    tab === "patch" ? patchScanDeviceStatus : inventoryScanStatusArr;
  const value = getEffectiveScanStatus(statusSource, record);

  let color;
  switch (value) {
    case "Initiated":
    case "In Progress":
      color = "blue";
      break;
    case "Success":
      color = "green";
      break;
    case "Failed":
      color = "red";
      break;
    case "Timed Out":
      color = "orange";
      break;
    default:
      color = "black";
  }

  if (value === "In Progress") {
    return (
      <span
        style={{
          display: "flex",
          width: "12ch",
          color: "blue",
        }}
      >
        <ScanInprogressHelper value={value} />
      </span>
    );
  }

  return <span style={{ color }}>{value}</span>;
};

const Remarks = ({ record }) => {
  const deviceScanStatusArr = useSelector(
    (state) => state.inventory.deviceScanStatusArr,
  );
  const deviceStatusFromStore = getDeviceScanState(deviceScanStatusArr, record);

  const value = deviceStatusFromStore
    ? deviceStatusFromStore.remarks
    : record.remarks;

  return <span>{value}</span>;
};
const ScanInprogressHelper = ({ value }) => {
  const [showProgress, setShowProgress] = useState(true);

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      setShowProgress(false);
    }, 500);
    return () => clearTimeout(timeout);
  }, []);

  return showProgress ? (
    <CircularProgress size={20} thickness={5} style={{ color: "#2f7ee9" }} />
  ) : (
    value
  );
};

export const columns = [
  {
    title: "Device Name",
    accessor: "deviceName",
    WrapperComponent: ManagedDevice,
  },
  { title: "User Name", accessor: "userName" },
  { title: "Domain", accessor: "domain" },
  {
    title: "Last Scan",
    accessor: "lastActionTime",
    unsearchable: true,
  },
  { title: "Scan Status", accessor: "status", UIComponent: ScanStatus },
  { title: "Remarks", accessor: "remarks", UIComponent: Remarks, width: 25 },
  {
    title: "Operating System",
    accessor: "operatingSystem",
    initiallyHidden: true,
  },
  { title: "IP Address", accessor: "ipAddress", initiallyHidden: true },
  { title: "MAC Address", accessor: "macaddress", initiallyHidden: true },
  {
    title: "Logged On Users",
    accessor: "currentlyLoggedOnUser",
    initiallyHidden: true,
  },
  { title: "Device Type", accessor: "deviceTypeCategory", initiallyHidden: true },
  { title: "Agent Version", accessor: "agentVersion", initiallyHidden: true },
  {
    title: "Agent Installed Time",
    accessor: "agentInstalledTime",
    initiallyHidden: true,
    unsearchable: true,
  },
  {
    title: "Agent Last Upgraded Time",
    accessor: "agentUpgradedTime",
    initiallyHidden: true,
    unsearchable: true,
  },
];

export const getScanDevicesFilterConfig = (
  activeFilters = {},
  activeFiltersKey,
) => ({
  active_filters: activeFilters,
  activeFiltersKey,
  comprehensive_filters: [
    {
      heading: "Platform",
      column: "operatingSystem",
      type: "multiple",
      options: [
        { label: "Windows", value: "Windows" },
        { label: "Mac", value: "MAC" },
        { label: "Linux", value: "Linux" },
      ],
    },
    {
      heading: "Live Status",
      column: "Live Status",
      type: "single",
      options: [
        { label: "Online", value: ["online"] },
        { label: "Offline", value: ["offline"] },
      ],
    },
    {
      heading: "Scan Status",
      column: "status",
      type: "multiple",
      options: [
        { label: "Success", value: "Success" },
        { label: "Failed", value: "Failed" },
        { label: "In Progress", value: "In Progress" },
        { label: "Timed Out", value: "Timed Out" },
      ],
    },
  ],
});

export const filterConfig = getScanDevicesFilterConfig();

const ScanDevices = ({
  tab = "inventory",
  activeFilters,
  activeFiltersKey,
}) => {
  const location = useLocation();
  const { checkRestriction } = useRestrictionChecker();
  const [scanFilter, setScanFilter] = useState(
    () => activeFilters || location.state?.active_filters || {},
  );
  const [scanFilterKey, setScanFilterKey] = useState(
    () => activeFiltersKey || location.state?.activeFiltersKey || 0,
  );
  const [isScanAllModalOpen, setIsScanAllModalOpen] = useState(false);
  const [loadingScanAll, setLoadingScanAll] = useState(false);
  const [tableRefreshKey, setTableRefreshKey] = useState(0);
  const [lastScanKey, setLastScanKey] = useState(0);

  const clearSelectionRef = useRef();

  useEffect(() => {
    if (!location.state?.active_filters) return;

    setScanFilter(location.state.active_filters);
    setScanFilterKey(location.state.activeFiltersKey || Date.now());
  }, [location.state]);

  const postScheduleNotifier = {
    patch: PostPatchScanNotifier,
  };

  const handleRefreshLastScanKey = useCallback(() => {
    setLastScanKey((prev) => prev + 1);
  }, []);

  const handleScanDevice = async (selectedDevices) => {
    if (checkRestriction("SCAN_SINGLE_DEVICE")) return;
    const selectedDeviceIds = selectedDevices.map((device) => device.id);
    const url = scanDeviceURL[tab];

    if (!url) return notify(<Failure>Something went wrong.</Failure>);

    try {
      const response = await EM_API.post(url, {
        deviceIds: selectedDeviceIds,
      });

      if (response.status.success) {
        handleClearSelected();
        handleRefreshLastScanKey();

        if (tab === "inventory") {
          selectedDeviceIds.forEach((id) => {
            updateDeviceScanStatus({
              deviceId: id,
              scanStatus: "In Progress",
              remarks: "Inventory Scan Operation in progress",
            });
          });
        } else if (tab === "patch") {
          const selectedDeviceIdsWithStatus = selectedDeviceIds.map((id) => ({
            deviceId: id,
            scanStatus: "In Progress",
            remarks: "Patch Scan Operation in progress",
          }));

          updatePatchScanDeviceStatus({
            deviceDetailsArr: selectedDeviceIdsWithStatus,
          });
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleClearSelected = () => {
    if (clearSelectionRef?.current?.clearSelected) {
      clearSelectionRef.current.clearSelected();
    }
  };

  const handleScanAllModalOpen = () => {
    if (checkRestriction("SCAN_ALL_DEVICE")) return;
    setIsScanAllModalOpen(true);
  };

  const handleScanAll = async () => {
    setLoadingScanAll(true);
    const url = scanAllDeviceURL[tab] || "";
    if (!url) return notify(<Failure>Something went wrong.</Failure>);
    try {
      const response = await EM_API.post(url);
      if (response.status.success) {
        handleClearSelected();
        handleRefreshLastScanKey();
        setTableRefreshKey((prev) => prev + 1);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingScanAll(false);
      setIsScanAllModalOpen(false);
    }
  };

  const columnsWithTab = React.useMemo(
    () =>
      columns.map((column) =>
        column.accessor === "status" ? { ...column, tab } : column,
      ),
    [tab],
  );

  const actionsConfigList = [];
  actionsConfigList.push({
    label: "Scan All",
    props: {
      onClick: handleScanAllModalOpen,
    },
  });
  actionsConfigList.push({
    label: "Scan Device",
    props: {
      onClick: handleScanDevice,
    },
    disableOnNoSelection: true,
    variant: "primary",
  });

  const Banner = postScheduleNotifier[tab];

  const [groupFilter, setGroupFilter] = useState(null);

  useEffect(() => {
    const getGroupFilter = async () => {
      try 
      {
        const filter = await getCommonGroupFilters();
        setGroupFilter(filter);
      } 
      catch (error) 
      {
        console.error("Error fetching group filters:", error);
      }
    };
    getGroupFilter();
  }, []);

  const tableFilterConfig = useMemo(() => {
    const config = getScanDevicesFilterConfig(scanFilter, scanFilterKey);
    config.comprehensive_filters = [...(groupFilter ? [groupFilter] : []),...config.comprehensive_filters];
    return config;
  }, [scanFilter, scanFilterKey, groupFilter]);

  return (
    <div
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "0.8rem",
        overflowY: "auto",
      }}
    >
      {
        Banner && <Banner lastScanKey={lastScanKey} />
      }
      <div
        style={{
          flex: 1,
          minHeight: 0,
        }}
      >
        <TableManager
          columns={columnsWithTab}
          filterConfig={tableFilterConfig}
          url={tableURLMap[tab] || ""}
          headingText="Total Devices : "
          refreshKey={tableRefreshKey}
          selectable
          actionsConfig={actionsConfigList}
          customeClearSelectedRowsRef={clearSelectionRef}
        />
      </div>

      {/* Message Modal for Scan All */}
      <Message
        isOpen={isScanAllModalOpen}
        onClose={() => setIsScanAllModalOpen(false)}
        title="Scan All Devices"
        description="Are you sure you want to initiate a scan for all devices?"
        confirmType="confirm"
        onConfirm={handleScanAll}
        onCancel={() => setIsScanAllModalOpen(false)}
        loading={loadingScanAll}
      />
    </div>
  );
};

export default ScanDevices;
